import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@slumbr/ui";

export interface ContextMenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export interface ContextMenuSection {
  items: ContextMenuItem[];
}

export interface ContextMenuAnchor {
  clientX: number;
  clientY: number;
}

interface Props {
  anchor: ContextMenuAnchor;
  sections: ContextMenuSection[];
  onClose: () => void;
}

const MENU_WIDTH = 200;

/** Floating right-click menu, portal-rendered + click-outside-to-close.
 *  Pass it `sections` so dividers fall in the right place; flatten items
 *  into one section when you don't care. */
export function ContextMenu({ anchor, sections, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const handleContext = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKey);
    document.addEventListener("contextmenu", handleContext, { capture: true });
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("contextmenu", handleContext, { capture: true });
    };
  }, [onClose]);

  const flat = sections.flatMap((section) => section.items);
  if (flat.length === 0) return null;

  const left = clampHorizontal(anchor.clientX);
  const top = clampVertical(anchor.clientY, sections);

  const content = (
    <div
      ref={ref}
      role="menu"
      style={{ left, top, width: MENU_WIDTH }}
      className="pointer-events-auto fixed z-[1000] overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0b0d0f]/95 py-1 shadow-2xl shadow-black/60 backdrop-blur-md"
    >
      {sections.map((section, sectionIdx) => (
        <div key={sectionIdx}>
          {sectionIdx > 0 && (
            <div className="my-1 h-px bg-white/[0.06]" aria-hidden="true" />
          )}
          {section.items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return;
                onClose();
                item.onSelect();
              }}
              className={cn(
                "flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm font-medium transition",
                item.danger
                  ? "text-[#ff477e] hover:bg-[#ff477e]/15"
                  : "text-[#d7dbdc] hover:bg-[#ff6b8f]/12 hover:text-[#ff8fac]",
                item.disabled && "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-[#d7dbdc]"
              )}
            >
              {item.icon && (
                <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                  {item.icon}
                </span>
              )}
              <span className="truncate">{item.label}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );

  return createPortal(content, document.body);
}

function clampHorizontal(value: number): number {
  if (typeof window === "undefined") return Math.max(8, value);
  return Math.max(8, Math.min(value, window.innerWidth - MENU_WIDTH - 8));
}

function clampVertical(value: number, sections: ContextMenuSection[]): number {
  if (typeof window === "undefined") return Math.max(8, value);
  const rowCount = sections.reduce((sum, section) => sum + section.items.length, 0);
  const dividerCount = Math.max(0, sections.length - 1);
  const height = 8 + rowCount * 30 + dividerCount * 9;
  return Math.max(8, Math.min(value, window.innerHeight - height - 8));
}
